import { MarketplaceUser } from 'APIs/marketplace.users/marketplace.users.types';
import Spinner from 'Components/Spinner/Spinner';
import { DateTime } from 'luxon';
import { FunctionComponent, useMemo } from 'react';
import DataTable, { TableColumn, createTheme } from 'react-data-table-component';
import { FaHourglassHalf } from 'react-icons/fa';
import { FaRegEdit } from 'react-icons/fa';
import { RiMailSendLine } from 'react-icons/ri';

import EmptyComponent from './EmptyComponent';
import ErrorComponent from './ErrorComponent';
import { TableStyleSettings, TableThemeSettings } from './constants';
import { ActionButton, ActionWaiting, MessageWrapper, UserDataTableContainer } from './styles';

createTheme('polarized', TableThemeSettings);

interface Props {
  userList?: Array<MarketplaceUser>;
  fetchListLoader: boolean;
  isErrorRequest: boolean;
  handleEditUser: (userId: string) => void;
  handleResendEmail: (userId: string) => void;
  actionUsers: Array<string>;
}

function UserDataTable({
  userList,
  fetchListLoader,
  isErrorRequest,
  handleEditUser,
  handleResendEmail,
  actionUsers,
}: Props) {
  const SendEmailIcon = RiMailSendLine as FunctionComponent;
  const HourGlassIcon = FaHourglassHalf as FunctionComponent;
  const EditIcon = FaRegEdit as FunctionComponent;

  const columns: TableColumn<MarketplaceUser>[] = useMemo(
    () => [
      {
        name: 'Name',
        selector: (row: MarketplaceUser) => `${row.firstName} ${row.lastName}`,
        grow: 5,
      },
      {
        name: 'Email',
        selector: (row: MarketplaceUser) => row.emailAddress,
        grow: 5,
      },
      {
        name: 'Role',
        selector: (row: MarketplaceUser) => row.userRole,
        grow: 1,
      },
      {
        name: 'Status',
        selector: (row: MarketplaceUser) => row.status,
        grow: 1,
      },
      {
        name: 'Created at',
        cell: (row: MarketplaceUser) => `${DateTime.fromISO(row.createdAt).toFormat('dd/LL/yyyy, HH:mm')}`,
        grow: 2,
      },
      {
        name: 'Updated at',
        cell: (row: MarketplaceUser) => `${DateTime.fromISO(row.updatedAt).toFormat('dd/LL/yyyy, HH:mm')}`,
        grow: 2,
      },
      {
        name: '',
        cell: (row: MarketplaceUser) => (
          <div>
            {actionUsers && actionUsers.includes(row._id) ? (
              <ActionWaiting>
                <HourGlassIcon />
              </ActionWaiting>
            ) : (
              <div className="hover-button-wrapper">
                <ActionButton className="hover-button" title="Resend email" onClick={() => handleResendEmail(row._id)}>
                  <SendEmailIcon />
                </ActionButton>
                <ActionButton className="hover-button" title="Edit" onClick={() => handleEditUser(row._id)}>
                  <EditIcon />
                </ActionButton>
              </div>
            )}
          </div>
        ),
        grow: 2,
        center: true,
      },
    ],
    [EditIcon, HourGlassIcon, SendEmailIcon, actionUsers, handleEditUser, handleResendEmail],
  );

  return (
    <UserDataTableContainer>
      <DataTable
        pagination={false}
        responsive
        columns={columns}
        data={userList ?? []}
        customStyles={TableStyleSettings}
        theme="polarized"
        className=""
        progressPending={fetchListLoader}
        progressComponent={
          <MessageWrapper>
            <Spinner color="#0016D1" height="25px" width="25px" />
          </MessageWrapper>
        }
        noDataComponent={
          isErrorRequest ? (
            <MessageWrapper>
              <ErrorComponent />
            </MessageWrapper>
          ) : (
            <MessageWrapper>
              <EmptyComponent />
            </MessageWrapper>
          )
        }
        paginationServer
        sortServer
        persistTableHead
        defaultSortAsc={false}
      />
    </UserDataTableContainer>
  );
}

export default UserDataTable;
